import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2, Tag } from 'lucide-react';
import { fetchDealsPageList, PublicDeal } from '../api/deals';
import {
  DealsPageHero,
  DealEndsBadge,
  dealBannerUrl,
  dealProductImage,
  LoadingDealsGrid,
} from '../components/deals/DealsUi';

function productCount(deal: PublicDeal): number {
  if (deal.products?.length) return deal.products.length;
  return (
    deal.sections?.reduce((n, s) => n + (s.products?.length || 0), 0) || 0
  );
}

function cardImage(deal: PublicDeal): string {
  const banner = dealBannerUrl(deal.bannerImage || deal.bannerMobileImage);
  if (banner) return banner;
  const preview = deal.products?.[0]?.product;
  if (preview) return dealProductImage(preview);
  return '';
}

const DealsPage: React.FC = () => {
  const [deals, setDeals] = useState<PublicDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDealsPageList()
      .then(setDeals)
      .catch((e) => setError(e.message || 'Failed to load deals'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 pt-[72px] sm:pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DealsPageHero />

        {loading && (
          <div className="flex flex-col items-center gap-6">
            <Loader2 className="w-9 h-9 animate-spin text-orange-500" />
            <LoadingDealsGrid />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl text-sm">
            {error}
          </div>
        )}

        {!loading && !error && deals.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-8 py-16 text-center">
            <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800">No active deals right now</h2>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">
              New offers are added regularly. Browse our catalog or check back soon.
            </p>
            <Link
              to="/products"
              className="inline-flex mt-6 items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-slate-800 transition-colors"
            >
              Shop all products
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {!loading && !error && deals.length > 0 && (
          <>
            <p className="text-sm text-slate-600 mb-5">
              <span className="font-semibold text-slate-900">{deals.length}</span>{' '}
              {deals.length === 1 ? 'deal' : 'deals'} available now
            </p>
            <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {deals.map((deal) => {
                const img = cardImage(deal);
                const count = productCount(deal);

                return (
                  <Link
                    key={deal._id}
                    to={`/deals/${deal.slug}`}
                    className="group flex flex-col bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-slate-200/60 hover:border-orange-200/80 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-500 to-red-600 overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Tag className="w-14 h-14 text-white/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      {deal.displayLabel && (
                        <span className="absolute top-3 left-3 bg-white text-red-600 text-xs font-bold px-2.5 py-1 rounded-lg shadow-md">
                          {deal.displayLabel}
                        </span>
                      )}
                      {count > 0 && (
                        <span className="absolute bottom-3 right-3 bg-black/55 backdrop-blur text-white text-xs font-medium px-2.5 py-1 rounded-lg">
                          {count} products
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col flex-1 p-5 sm:p-6">
                      <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-snug group-hover:text-orange-600 transition-colors line-clamp-2">
                        {deal.title}
                      </h2>
                      {deal.subtitle && (
                        <p className="text-slate-500 text-sm mt-2 line-clamp-2 leading-relaxed">
                          {deal.subtitle}
                        </p>
                      )}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                        <DealEndsBadge endDate={deal.endDate} />
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 group-hover:gap-2 transition-all">
                          View deal
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DealsPage;
